import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from './auth-user'
import { isJson } from '@src/helpers/GlobalFunctions';
import { GetOfflineConnections, GetOnlineConnections } from '@src/api/users';
// import { toast } from '@src/components/ToastExtended';

const UserProvider = ({ children }) => {
    const { isLogin, hasLogout, userData } = useContext(AuthContext)

    const [online, setOnline] = useState(false);
    const onlineSocket = useMemo(() => {

        const token = localStorage.getItem("Token");
        const socket_url = process.env.REACT_APP_WEBSOCKET;

        if (token && socket_url) {
            if (isLogin && userData && !userData.is_away) {
                return new WebSocket(`${socket_url}/set-user-status/?token=${token}`);
            }
        }

        return null
    }, [isLogin, userData])

    const disconnectUserSocket = useMemo(() => {
        if ((userData && userData.is_away) || hasLogout) return true
        return false
    }, [hasLogout, userData])

    useEffect(() => {
        if (onlineSocket) {
            onlineSocket.onopen = function (e) {
                console.log("Connection established! (1)", e);
                setOnline(true);
            };

            onlineSocket.onclose = async function (e) {
                console.log('close (1)', e)
                setOnline(false);
            }
        }


        return () => {
            if (onlineSocket && onlineSocket.readyState === 1) {
                onlineSocket.close();
            }
        }
    }, [onlineSocket])

    useEffect(() => {
        if (disconnectUserSocket) {
            if (onlineSocket && onlineSocket.readyState === 1) {
                onlineSocket.close();
            }
        }
    }, [disconnectUserSocket, onlineSocket])


    const [socket, setSocket] = useState(null);
    const [serverMessage, setServerMessage] = useState("");
    const [webSocketReady, setWebSocketReady] = useState(false);

    const [connections, setConnections] = useState({
        online: [],
        offline: [],
    })


    const fetchConnectionsList = React.useCallback(() => {
        return Promise.all([
            GetOnlineConnections(),
            GetOfflineConnections()
        ]).then((responses) => {
            const onlineData = responses[0]?.data ?? [];
            const offlineData = responses[1]?.data ?? [];
            const arr = [...onlineData, ...offlineData];
            const uniq = (list) => list.filter((v, i, self) => i === self.findIndex((t) => t.id === v.id));
            const newOffline = uniq(arr).filter((v) => v.online <= 0);
            const newOnline = uniq(arr).filter((v) => v.online > 0 && !v.is_away);
            const newOnlineIds = new Set(newOnline.map((c) => c.id));
            const newOfflineIds = new Set(newOffline.map((c) => c.id));
            setConnections(prev => {
                const prevOnlineIds = new Set((prev.online ?? []).map((c) => c.id));
                const prevOfflineIds = new Set((prev.offline ?? []).map((c) => c.id));
                const onlineChanged =
                    newOnlineIds.size !== prevOnlineIds.size ||
                    [...newOnlineIds].some((id) => !prevOnlineIds.has(id)) ||
                    [...prevOnlineIds].some((id) => !newOnlineIds.has(id));
                const offlineChanged =
                    newOfflineIds.size !== prevOfflineIds.size ||
                    [...newOfflineIds].some((id) => !prevOfflineIds.has(id)) ||
                    [...prevOfflineIds].some((id) => !newOfflineIds.has(id));
                if (!onlineChanged && !offlineChanged) return prev;
                return { online: newOnline, offline: newOffline };
            });
        }).catch(() => {});
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("Token");
        if (token && isLogin) {
            fetchConnectionsList().finally(() => {
                const socket_url = process.env.REACT_APP_WEBSOCKET;
                if (socket_url) {
                    setSocket(new WebSocket(`${socket_url}/my-connected-users/?token=${token}`));
                }
            });
        }
    }, [isLogin, fetchConnectionsList]);

    const REFRESH_CONNECTIONS_INTERVAL_MS = 45000;

    useEffect(() => {
        if (!isLogin) return;
        const token = localStorage.getItem("Token");
        if (!token) return;
        const intervalId = setInterval(fetchConnectionsList, REFRESH_CONNECTIONS_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [isLogin, fetchConnectionsList]);

    useEffect(() => {

        if (socket) {

            socket.onopen = function (e) {
                setWebSocketReady(true);
                console.log("Connection established! (2)", e);
            };

            socket.onclose = async function (e) {
                console.log('close (2)', e)
                setWebSocketReady(false);
            }

            socket.onmessage = async function (e) {
                console.log('message (2)', e)
                setServerMessage(e.data)
            }

            socket.onerror = function (err) {
                console.log('Socket encountered error: ', err.message, 'Closing socket');
                setWebSocketReady(false);
                socket.close();
            };

            if (hasLogout) {
                socket.close();
                setConnections({
                    online: [],
                    offline: [],
                })
            }
        }

        return () => {
            if (socket) {
                socket.close();
            }
        }
    }, [socket, hasLogout])

    useEffect(() => {
        if (serverMessage !== "" && isJson(serverMessage)) {
            const user = JSON.parse(serverMessage);
            const meUser = userData;
            if (user && meUser && user.id !== meUser.id) {
                setConnections(prev => {
                    const inOnline = prev.online.some((p) => p.id === user.id);
                    const inOffline = prev.offline.some((p) => p.id === user.id);
                    if (user.online > 0) {
                        const online = inOnline ? prev.online : [...prev.online, user];
                        const offline = inOffline ? prev.offline.filter((off) => off.id !== user.id) : prev.offline;
                        return { online, offline };
                    } else {
                        const offline = inOffline ? prev.offline : [...prev.offline, user];
                        const online = inOnline ? prev.online.filter((oln) => oln.id !== user.id) : prev.online;
                        return { online, offline };
                    }
                })
            }
        }
    }, [serverMessage, userData])

    return (
        <UserContext.Provider
            value={{
                online,
                connections: connections,
                socketReady: webSocketReady,
            }}
        > {children} </UserContext.Provider>
    )
}

export const UserContext = createContext({})
export default UserProvider
